import type { Contact, ExperienceEntry } from './types';

export function titleCase(s: string | undefined | null): string {
  if (!s) return '';
  return s.split(/\s+/).map(w => w.length ? w[0].toUpperCase() + w.slice(1) : w).join(' ');
}

export function compactNumber(n: number): string {
  if (n >= 1000) return Math.round(n / 100) / 10 + 'k';
  return String(n);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function parseYM(s: string | undefined | null): { y: number; m?: number } | null {
  if (!s) return null;
  const m = /^(\d{4})(?:-(\d{1,2}))?/.exec(s);
  if (!m) return null;
  return { y: Number(m[1]), m: m[2] ? Number(m[2]) : undefined };
}
function fmtYM(s: string | undefined | null): string {
  const p = parseYM(s);
  if (!p) return '';
  return p.m ? `${MONTHS[p.m - 1]} ${p.y}` : String(p.y);
}

export function dateRange(start?: string, end?: string): string {
  const s = fmtYM(start);
  const e = end && end.trim() ? fmtYM(end) : 'Present';
  if (!s) return e;
  return `${s} – ${e}`;
}

export function durationMonths(start?: string, end?: string): number {
  const s = parseYM(start);
  if (!s) return 0;
  const e = end && end.trim() ? parseYM(end) : { y: new Date().getFullYear(), m: new Date().getMonth() + 1 };
  if (!e) return 0;
  return Math.max(0, (e.y - s.y) * 12 + ((e.m || 1) - (s.m || 1)));
}

export function fmtDuration(months: number): string {
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  if (yrs && mos) return `${yrs} yr${yrs > 1 ? 's' : ''} ${mos} mo${mos > 1 ? 's' : ''}`;
  if (yrs) return `${yrs} yr${yrs > 1 ? 's' : ''}`;
  return `${mos} mo${mos > 1 ? 's' : ''}`;
}

export function totalExperienceMonths(c: Contact): number {
  return (c.profileData.experience || []).reduce((acc, x) => acc + durationMonths(x.start_date, x.end_date), 0);
}

export function avgTenureMonths(c: Contact): number {
  const exp = c.profileData.experience || [];
  if (!exp.length) return 0;
  return Math.round(totalExperienceMonths(c) / exp.length);
}

export function currentRoleMonths(c: Contact): number {
  const exp = c.profileData.experience || [];
  const cur = exp.find(e => e.is_primary) || exp[0];
  if (!cur) return 0;
  return durationMonths(cur.start_date, cur.end_date);
}

export function currentRole(c: Contact): ExperienceEntry | undefined {
  return (c.profileData.experience || []).find(e => e.is_primary) || c.profileData.experience?.[0];
}

export function highestEducation(c: Contact): { school?: string; degree?: string; major?: string } {
  const ed = c.profileData.education?.[0];
  if (!ed) return {};
  return {
    school: titleCase(ed.school?.name),
    degree: titleCase(ed.degrees?.[0]),
    major: titleCase(ed.majors?.[0]),
  };
}

export function initials(name: string): string {
  return name.trim().split(/\s+/).map(p => p[0] || '').join('').slice(0, 2).toUpperCase();
}
